import { useEffect, useState } from "react";
import { useDogs } from "@/context/DogsContext";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { SectionTitle } from "@/components/app/Bits";
import BrandHero from "@/components/app/BrandHero";
import EmptyDogState from "@/components/app/EmptyDogState";
import UserTypesEditCard from "@/components/app/UserTypesEditCard";
import VetContactsCard from "@/components/app/VetContactsCard";
import { MAX_VET_FILE_BYTES } from "@/lib/constants";
import { toast } from "sonner";
import { Download, Trash2, FileText, Image as ImageIcon, Stethoscope, Pencil } from "lucide-react";
import { format, parseISO } from "date-fns";
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid } from "recharts";

async function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

export default function VetAndHealthPage() {
  const { activeDog, refresh } = useDogs();
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ title: "", record_type: "vet", record_date: format(new Date(), "yyyy-MM-dd"), notes: "", file_data: "", file_name: "", mime_type: "" });
  const [editingId, setEditingId] = useState(null);   // record id being edited, or null
  const [saving, setSaving] = useState(false);

  // Weight & food state
  const [weights, setWeights] = useState([]);
  const [newW, setNewW] = useState("");
  const [newDate, setNewDate] = useState(() => format(new Date(), "yyyy-MM-dd"));
  const [unit, setUnit] = useState("lb");
  const [food, setFood] = useState({ food_type: "", food_amount: "", food_amount_am: "", food_amount_pm: "", supplements: "", height: "" });
  const [savingFood, setSavingFood] = useState(false);

  const loadAll = async () => {
    if (!activeDog) return;
    const [vr, ws] = await Promise.all([
      api.get("/vet-records", { params: { dog_id: activeDog.id } }),
      api.get("/weights", { params: { dog_id: activeDog.id } }),
    ]);
    setItems(vr.data.items || []);
    setWeights(ws.data.items || []);
  };

  useEffect(() => {
    if (!activeDog) return;
    setFood({
      food_type: activeDog.food_type || "",
      food_amount: activeDog.food_amount || "",
      food_amount_am: activeDog.food_amount_am || "",
      food_amount_pm: activeDog.food_amount_pm || "",
      supplements: activeDog.supplements || "",
      height: activeDog.height || "",
    });
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeDog]);

  if (!activeDog) return <EmptyDogState>{null}</EmptyDogState>;

  // ---- Vet records ----
  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_VET_FILE_BYTES) {
      toast.error("Please choose a file under 4 MB");
      return;
    }
    const data = await fileToDataUrl(file);
    setForm((f) => ({ ...f, file_data: data, file_name: file.name, mime_type: file.type }));
  };

  const submitVet = async (e) => {
    e.preventDefault();
    if (!form.title) return;
    setSaving(true);
    try {
      if (editingId) {
        await api.put(`/vet-records/${editingId}`, { dog_id: activeDog.id, ...form });
        toast.success("Record updated");
      } else {
        await api.post("/vet-records", { dog_id: activeDog.id, ...form });
        toast.success("Record added");
      }
      setForm({ title: "", record_type: "vet", record_date: format(new Date(), "yyyy-MM-dd"), notes: "", file_data: "", file_name: "", mime_type: "" });
      setEditingId(null);
      loadAll();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    } finally {
      setSaving(false);
    }
  };

  const startEditVet = (rec) => {
    setEditingId(rec.id);
    setForm({
      title: rec.title || "",
      record_type: rec.record_type || "vet",
      record_date: rec.record_date || format(new Date(), "yyyy-MM-dd"),
      notes: rec.notes || "",
      // Blank file fields — backend keeps existing file if left blank.
      file_data: "",
      file_name: "",
      mime_type: "",
    });
    // Scroll the edit form into view so the user sees where the edit lands.
    setTimeout(() => {
      document.querySelector('[data-testid="add-vet-form"]')?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 50);
  };

  const cancelEditVet = () => {
    setEditingId(null);
    setForm({ title: "", record_type: "vet", record_date: format(new Date(), "yyyy-MM-dd"), notes: "", file_data: "", file_name: "", mime_type: "" });
  };

  const removeVet = async (id) => {
    if (!window.confirm("Delete this record?")) return;
    await api.delete(`/vet-records/${id}`);
    if (editingId === id) cancelEditVet();
    loadAll();
  };

  const downloadVet = async (rec) => {
    try {
      const { data } = await api.get(`/vet-records/${rec.id}`);
      const a = document.createElement("a");
      a.href = data.file_data;
      a.download = data.file_name || "record";
      a.click();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Download failed");
    }
  };

  // ---- Weight ----
  const addWeight = async (e) => {
    e.preventDefault();
    const w = parseFloat(newW);
    if (!w) return;
    try {
      const { data } = await api.post("/weights", { dog_id: activeDog.id, weight: w, unit, entry_date: newDate });
      setWeights((cur) => [...cur, data].sort((a, b) => a.entry_date.localeCompare(b.entry_date)));
      setNewW("");
      toast.success("Weight added");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    }
  };

  const removeWeight = async (id) => {
    await api.delete(`/weights/${id}`);
    setWeights((cur) => cur.filter((x) => x.id !== id));
  };

  // ---- Food ----
  const saveFood = async () => {
    setSavingFood(true);
    try {
      const payload = { ...activeDog };
      delete payload.id; delete payload.owner_id; delete payload.shared;
      delete payload.share_permission; delete payload.created_at; delete payload.updated_at;
      Object.assign(payload, food);
      await api.put(`/dogs/${activeDog.id}`, payload);
      toast.success("Food info saved");
      await refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    } finally {
      setSavingFood(false);
    }
  };

  const chartData = weights.map((w) => ({ date: w.entry_date, weight: w.weight }));

  return (
    <div className="space-y-12" data-testid="vet-and-health-page">
      <BrandHero
        overline="Care"
        title="Health & Vet"
        description={(<>Weight, food, supplements, and vet records — all in one place for <strong>{activeDog?.name || "your dog"}</strong>.</>)}
        testid="vet-health-hero"
      />

      {/* Vets & specialists — multi-contact card list. Sits high on the page
          so the phone number is one scroll away in a pinch. */}
      <VetContactsCard dogId={activeDog.id} />

      {/* Food & supplements */}
      <div>
        <div className="flex items-end justify-between mb-4">
          <h3 className="font-display text-xl font-bold tracking-tight">Measurements, food & supplements</h3>
          <Button onClick={saveFood} disabled={savingFood} className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="save-food-button">
            {savingFood ? "Saving…" : "Save"}
          </Button>
        </div>
        <Card className="border-border shadow-none rounded-lg">
          <CardContent className="p-6 sm:p-8 grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label>Height</Label>
              <Input
                value={food.height}
                onChange={(e) => setFood({ ...food, height: e.target.value })}
                placeholder="e.g. 24 in / 61 cm"
                className="mt-1.5"
                data-testid="height-input"
              />
            </div>
            <div>
              <Label>Food type / brand</Label>
              <Input value={food.food_type} onChange={(e) => setFood({ ...food, food_type: e.target.value })} className="mt-1.5" data-testid="food-type-input" />
            </div>
            <div>
              <Label>Amount per day (total)</Label>
              <Input value={food.food_amount} onChange={(e) => setFood({ ...food, food_amount: e.target.value })} placeholder="e.g. 2 cups" className="mt-1.5" data-testid="food-amount-input" />
            </div>
            <div>
              <Label>AM feed amount</Label>
              <Input
                value={food.food_amount_am}
                onChange={(e) => setFood({ ...food, food_amount_am: e.target.value })}
                placeholder="e.g. 1 cup"
                className="mt-1.5"
                data-testid="food-amount-am-input"
              />
            </div>
            <div>
              <Label>PM feed amount</Label>
              <Input
                value={food.food_amount_pm}
                onChange={(e) => setFood({ ...food, food_amount_pm: e.target.value })}
                placeholder="e.g. 1 cup"
                className="mt-1.5"
                data-testid="food-amount-pm-input"
              />
            </div>
            <div className="md:col-span-3">
              <Label>Supplements</Label>
              <Textarea rows={3} value={food.supplements} onChange={(e) => setFood({ ...food, supplements: e.target.value })} className="mt-1.5" placeholder="Fish oil, joint support, probiotics…" data-testid="supplements-input" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Weight tracking */}
      <div>
        <h3 className="font-display text-xl font-bold tracking-tight mb-4">Weight tracking</h3>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
          <Card className="lg:col-span-2 border-border shadow-none rounded-lg">
            <CardContent className="p-6">
              {chartData.length ? (
                <div className="h-72">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={chartData}>
                      <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" />
                      <XAxis dataKey="date" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
                      <YAxis tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
                      <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8 }} />
                      <Line type="monotone" dataKey="weight" stroke="hsl(var(--primary))" strokeWidth={2} dot={{ r: 3 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground py-12 text-center">No weight entries yet.</p>
              )}
            </CardContent>
          </Card>

          <Card className="border-border shadow-none rounded-lg">
            <CardContent className="p-6">
              <p className="font-medium mb-4">Add a weight</p>
              <form onSubmit={addWeight} className="space-y-3" data-testid="add-weight-form">
                <div>
                  <Label>Weight</Label>
                  <div className="flex gap-2 mt-1.5">
                    <Input type="number" step="0.1" value={newW} onChange={(e) => setNewW(e.target.value)} required data-testid="weight-input" />
                    <select value={unit} onChange={(e) => setUnit(e.target.value)} className="border border-border rounded-md bg-card px-2 text-sm">
                      <option value="lb">lb</option>
                      <option value="kg">kg</option>
                    </select>
                  </div>
                </div>
                <div>
                  <Label>Date</Label>
                  <Input type="date" value={newDate} onChange={(e) => setNewDate(e.target.value)} className="mt-1.5" />
                </div>
                <Button type="submit" className="w-full bg-primary text-primary-foreground hover:bg-primary/90" data-testid="add-weight-button">Add</Button>
              </form>
            </CardContent>
          </Card>
        </div>

        {weights.length > 0 && (
          <Card className="mt-6 border-border shadow-none rounded-lg">
            <CardContent className="p-0">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground">
                    <th className="px-6 py-3">Date</th>
                    <th className="px-6 py-3">Weight</th>
                    <th className="px-6 py-3 w-12"></th>
                  </tr>
                </thead>
                <tbody>
                  {[...weights].reverse().map((w) => (
                    <tr key={w.id} className="border-t border-border" data-testid={`weight-row-${w.id}`}>
                      <td className="px-6 py-3">{format(parseISO(w.entry_date), "MMM d, yyyy")}</td>
                      <td className="px-6 py-3">{w.weight} {w.unit}</td>
                      <td className="px-6 py-3 text-right">
                        <button onClick={() => removeWeight(w.id)} className="text-muted-foreground hover:text-destructive" title="Remove">
                          <Trash2 size={15} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        )}
      </div>

      {/* User-type picker — sits up top so the user finds it without scrolling.
          Visible to everyone (not gated). Edits go to /auth/profile. */}
      <UserTypesEditCard overline="About you" />

      {/* X-Rays — breeder-only dedicated section sits above general vet files */}
      {user?.is_breeder && <XRaySection items={items} onChanged={loadAll} dog={activeDog} />}

      {/* Vet & health files */}
      <div>
        <h3 className="font-display text-xl font-bold tracking-tight mb-4">Health &amp; vet files</h3>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <Card className="lg:col-span-2 border-border shadow-none rounded-lg">
            <CardContent className="p-0">
              {items.length === 0 ? (
                <p className="text-sm text-muted-foreground p-10 text-center">No records yet. Add your first one →</p>
              ) : (
                <ul className="divide-y divide-border">
                  {items.map((rec) => (
                    <li key={rec.id} className="p-5 flex items-start gap-4" data-testid={`vet-row-${rec.id}`}>
                      <div className="h-10 w-10 rounded-md bg-muted flex items-center justify-center text-muted-foreground">
                        <FileText size={18} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-medium">{rec.title}</p>
                          <span className="text-xs uppercase tracking-wider px-1.5 py-0.5 rounded bg-muted text-muted-foreground">{rec.record_type}</span>
                        </div>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          {rec.record_date ? format(parseISO(rec.record_date), "MMM d, yyyy") : "No date"}
                          {rec.file_name ? ` · ${rec.file_name}` : ""}
                        </p>
                        {rec.notes ? <p className="text-sm mt-2 text-foreground/80">{rec.notes}</p> : null}
                      </div>
                      <div className="flex items-center gap-1">
                        {rec.file_data || rec.file_name ? (
                          <button type="button" onClick={() => downloadVet(rec)} className="p-2 hover:bg-muted rounded-md" title="Download" data-testid={`vet-download-${rec.id}`}>
                            <Download size={16} />
                          </button>
                        ) : null}
                        <button type="button" onClick={() => startEditVet(rec)} className="p-2 hover:bg-muted rounded-md text-muted-foreground hover:text-primary" title="Edit" data-testid={`vet-edit-${rec.id}`}>
                          <Pencil size={16} />
                        </button>
                        <button onClick={() => removeVet(rec.id)} className="p-2 hover:bg-muted rounded-md text-muted-foreground hover:text-destructive" title="Delete" data-testid={`vet-delete-${rec.id}`}>
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>

          <Card className="border-border shadow-none rounded-lg">
            <CardContent className="p-6">
              <p className="font-medium mb-4" data-testid="vet-form-heading">{editingId ? "Edit record" : "Add record"}</p>
              <form onSubmit={submitVet} className="space-y-3" data-testid="add-vet-form">
                <div>
                  <Label>Title</Label>
                  <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required className="mt-1.5" data-testid="vet-title-input" />
                </div>
                <div>
                  <Label>Type</Label>
                  <select value={form.record_type} onChange={(e) => setForm({ ...form, record_type: e.target.value })} className="mt-1.5 w-full border border-border rounded-md bg-card px-3 py-2 text-sm">
                    <option value="vet">Vet visit</option>
                    <option value="vaccination">Vaccination</option>
                    <option value="health">Health/test</option>
                    <option value="allergies">Allergies</option>
                    {user?.is_breeder && <option value="xray">X-Ray</option>}
                    <option value="other">Other</option>
                  </select>
                </div>
                <div>
                  <Label>Date</Label>
                  <Input type="date" value={form.record_date} onChange={(e) => setForm({ ...form, record_date: e.target.value })} className="mt-1.5" />
                </div>
                <div>
                  <Label>File (PDF or image, ≤4 MB){editingId ? " — leave blank to keep existing" : ""}</Label>
                  <Input type="file" accept="image/*,application/pdf" onChange={onFile} className="mt-1.5" data-testid="vet-file-input" />
                  {editingId && form.file_name ? (
                    <p className="text-xs text-muted-foreground mt-1">New file selected: {form.file_name}</p>
                  ) : null}
                </div>
                <div>
                  <Label>Notes</Label>
                  <Textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1.5" />
                </div>
                <div className="flex gap-2">
                  {editingId ? (
                    <Button type="button" variant="outline" onClick={cancelEditVet} className="flex-1" data-testid="cancel-edit-vet-button">
                      Cancel
                    </Button>
                  ) : null}
                  <Button type="submit" disabled={saving} className="flex-1 bg-primary text-primary-foreground hover:bg-primary/90" data-testid="submit-vet-button">
                    {saving ? "Saving…" : editingId ? "Save changes" : "Add record"}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}

// ----------------------------------------------------------------
// Breeder-only X-ray section — uploads, viewing, and breeder notes.
// Records are written to the same /vet-records collection with
// record_type="xray" so they show up in the unified list too, but
// breeders get this dedicated view for quick comparison + notes.
// ----------------------------------------------------------------
function XRaySection({ items, onChanged, dog }) {
  const xrays = (items || []).filter((r) => r.record_type === "xray");
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState({ title: "", record_date: format(new Date(), "yyyy-MM-dd"), notes: "" });

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_VET_FILE_BYTES) {
      toast.error("Please choose a file under 4 MB");
      return;
    }
    setUploading(true);
    try {
      const data = await fileToDataUrl(file);
      const title = (form.title || "").trim() || (file.name.replace(/\.[^.]+$/, "") || "X-ray");
      await api.post("/vet-records", {
        dog_id: dog.id,
        title,
        record_type: "xray",
        record_date: form.record_date,
        notes: form.notes,
        file_data: data,
        file_name: file.name,
        mime_type: file.type,
      });
      toast.success("X-ray uploaded");
      setForm({ title: "", record_date: format(new Date(), "yyyy-MM-dd"), notes: "" });
      e.target.value = "";
      onChanged();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this X-ray?")) return;
    try {
      await api.delete(`/vet-records/${id}`);
      onChanged();
    } catch (err) {
      toast.error("Could not delete");
    }
  };

  const download = async (rec) => {
    try {
      const { data } = await api.get(`/vet-records/${rec.id}`);
      const a = document.createElement("a");
      a.href = data.file_data;
      a.download = data.file_name || `${rec.title}.png`;
      a.click();
    } catch (err) {
      toast.error("Download failed");
    }
  };

  return (
    <div data-testid="xray-section">
      <div className="flex items-center gap-2 mb-4">
        <Stethoscope size={18} className="text-primary" />
        <h3 className="font-display text-xl font-bold tracking-tight">X-rays &amp; breeder imaging</h3>
        <span className="text-xs uppercase tracking-wider px-2 py-0.5 rounded-full bg-secondary text-secondary-foreground border border-border">Breeder</span>
      </div>

      <Card className="border-dashed border-border shadow-none rounded-lg mb-4" data-testid="xray-upload-card">
        <CardContent className="p-5 sm:p-6">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3">
            <div className="sm:col-span-2">
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">Label</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Day 55 pregnancy X-ray" className="mt-1.5" data-testid="xray-title" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">Date</Label>
              <Input type="date" value={form.record_date} onChange={(e) => setForm({ ...form, record_date: e.target.value })} className="mt-1.5" data-testid="xray-date" />
            </div>
            <div className="sm:col-span-3">
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">Notes (puppy count, vet, follow-up)</Label>
              <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1.5" placeholder="6 puppies counted · Dr. Patel · recheck in 5 days" data-testid="xray-notes" />
            </div>
            <div className="sm:col-span-3">
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">Image or PDF (≤4 MB)</Label>
              <Input type="file" accept="image/*,application/pdf" onChange={onFile} disabled={uploading} className="mt-1.5" data-testid="xray-file" />
              {uploading && <p className="text-xs text-muted-foreground mt-2">Uploading…</p>}
            </div>
          </div>
        </CardContent>
      </Card>

      {xrays.length === 0 ? (
        <Card className="border-border shadow-none rounded-lg" data-testid="xray-empty">
          <CardContent className="p-6 text-sm text-muted-foreground text-center">
            No X-rays yet. Upload pregnancy or puppy imaging here to keep it separate from regular vet records.
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="xray-grid">
          {xrays.map((rec) => (
            <Card key={rec.id} className="border-border shadow-none rounded-lg overflow-hidden" data-testid={`xray-row-${rec.id}`}>
              <CardContent className="p-0">
                <div className="aspect-square bg-muted flex items-center justify-center text-muted-foreground">
                  <ImageIcon size={32} />
                </div>
                <div className="p-4">
                  <p className="font-medium text-sm truncate" data-testid={`xray-title-${rec.id}`}>{rec.title}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {rec.record_date ? format(parseISO(rec.record_date), "MMM d, yyyy") : "No date"}
                  </p>
                  {rec.notes && <p className="text-xs text-foreground/70 mt-2 whitespace-pre-wrap line-clamp-3">{rec.notes}</p>}
                  <div className="flex items-center gap-1 mt-3 -ml-1">
                    <button onClick={() => download(rec)} className="p-1.5 hover:bg-muted rounded-md text-muted-foreground" title="Download" data-testid={`xray-download-${rec.id}`}>
                      <Download size={14} />
                    </button>
                    <button onClick={() => remove(rec.id)} className="p-1.5 hover:bg-muted rounded-md text-muted-foreground hover:text-destructive" title="Delete" data-testid={`xray-delete-${rec.id}`}>
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
